import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Publication } from '@shared/types/publication';
import { cn } from '@shared/utils/utils';
import defaultCover from '@shared/assets/images/default-cover.png';
import { formatPublicationTitle } from '@shared/utils/publication';

interface FeaturedCarouselProps {
    items: Publication[],
    autoPlayInterval?: number
}

const FeaturedCarousel = ({ items, autoPlayInterval = 3000 }: FeaturedCarouselProps) => {
    const navigate = useNavigate();

    const [currentIndex, setCurrentIndex] = useState(0);
    const [isAnimating, setIsAnimating] = useState(false);

    const handleNext = () => {
        if (isAnimating || items.length <= 1) return;
        setIsAnimating(true);
        setCurrentIndex(prev => (prev + 1) % items.length);
    };

    const handlePrev = () => {
        if (isAnimating || items.length <= 1) return;
        setIsAnimating(true);
        setCurrentIndex(prev => (prev - 1 + items.length) % items.length);
    };

    // Reset animation state after transition
    useEffect(() => {
        if (isAnimating) {
            const timer = setTimeout(() => {
                setIsAnimating(false);
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [isAnimating, currentIndex]);

    // Auto-play effect
    useEffect(() => {
        if (!items || items.length <= 1) return;

        const intervalId = setInterval(() => {
            if (!isAnimating) {
                handleNext();
            }
        }, autoPlayInterval);

        return () => clearInterval(intervalId);
    }, [items, autoPlayInterval, isAnimating, currentIndex]);

    if (!items || items.length === 0) return null;

    const currentItem = items[currentIndex];

    // Calculate position for each slide based on its index relative to current
    const getSlidePosition = (index: number) => {
        const len = items.length;
        let diff = index - currentIndex;

        // Normalize to handle wrap around
        if (diff > len / 2) diff -= len;
        if (diff < -len / 2) diff += len;

        return diff;
    };

    const getSlideStyles = (index: number) => {
        const position = getSlidePosition(index);
        const baseClasses = 'absolute transition-all duration-500 ease-in-out';

        if (position === 0) {
            // Active slide - center, large
            return cn(
                baseClasses,
                'left-1/2 -translate-x-1/2 w-[174px] sm:w-[275px] h-[244px] sm:h-[385px] z-10 opacity-100'
            );
        } else if (position === -1) {
            // Previous slide - left, small with overlay
            return cn(
                baseClasses,
                'left-[5%] w-[123px] sm:w-[194px] h-[172px] sm:h-[271px] z-0 opacity-100'
            );
        } else if (position === 1) {
            // Next slide - right, small with overlay
            return cn(
                baseClasses,
                'right-[5%] w-[123px] sm:w-[194px] h-[172px] sm:h-[271px] z-0 opacity-100'
            );
        } else if (position === -2) {
            // Hidden left
            return cn(
                baseClasses,
                'left-0 -translate-x-full w-[123px] sm:w-[194px] h-[172px] sm:h-[271px] z-0 opacity-0'
            );
        } else if (position === 2) {
            // Hidden right
            return cn(
                baseClasses,
                'right-0 translate-x-full w-[123px] sm:w-[194px] h-[172px] sm:h-[271px] z-0 opacity-0'
            );
        } else {
            // Far away - hidden
            return cn(baseClasses, 'opacity-0 pointer-events-none');
        }
    };

    const shouldShowOverlay = (index: number) => {
        const position = getSlidePosition(index);
        return position !== 0; // Show overlay on non-active slides
    };

    // Get visible slide indices (current, prev, next, and their neighbors for smooth animation)
    const getVisibleIndices = () => {
        const len = items.length;
        const indices = new Set<number>();

        for (let offset = -2; offset <= 2; offset++) {
            indices.add(((currentIndex + offset) % len + len) % len);
        }

        return Array.from(indices);
    };

    return (
        <section className='bg-section-highlight'>
            <div className='container mx-auto px-4 flex flex-col lg:flex-row gap-6 md:gap-12 py-8 md:py-[60px]'>
                {/* Left Content */}
                <div className='flex-1 lg:w-1/2 text-left flex flex-col'>
                    <div className='w-[130px] h-[33px] flex items-center justify-center bg-[#FBC11A] font-semibold text-white leading-[29px] rounded mb-3'>
                        新刊・近刊
                    </div>
                    <h1 className='font-semibold text-[29px] leading-[36px] mb-3 line-clamp-2'>
                        {formatPublicationTitle(currentItem)}
                    </h1>
                    <p className='font-normal leading-[32px] whitespace-pre-line line-clamp-[7] mb-3'>
                        {currentItem.description}
                    </p>
                    <button onClick={() => navigate(`/publications/${currentItem.id}`)} className='hidden lg:block bg-primary hover:bg-primary-hover text-white font-semibold leading-[16px] w-[234px] py-4 px-8 rounded-[10px] transition-colors'>
                        詳細を見る
                    </button>
                </div>

                {/* Right Carousel */}
                <div className='flex-1 lg:w-1/2 relative flex flex-col items-center justify-center gap-8 w-full'>
                    {/* Navigation Arrows */}
                    <button
                        onClick={handlePrev}
                        className='hidden lg:block absolute left-[-20px] z-20 p-[5px] rounded-full border border-gray-200 bg-white shadow-sm hover:bg-gray-50 transition-colors'
                    >
                        <ChevronLeft className='size-5 text-[#262628]' />
                    </button>

                    <div className='relative flex items-center justify-center w-full h-[244px] sm:h-[385px] overflow-hidden'>
                        {getVisibleIndices().map(index => (
                            <div
                                key={items[index].id}
                                className={getSlideStyles(index)}
                            >
                                <img
                                    src={items[index].linkImg}
                                    alt={items[index].title}
                                    className='w-full h-full object-cover'
                                    onError={(e) => {
                                        e.currentTarget.src = defaultCover;
                                    }}
                                />
                                {shouldShowOverlay(index) && (
                                    <div className='absolute inset-0 bg-[#00000066] transition-opacity duration-500' />
                                )}
                            </div>
                        ))}
                    </div>

                    <button
                        onClick={handleNext}
                        className='hidden lg:block absolute right-[-20px] z-20 p-[5px] rounded-full border border-gray-200 bg-white shadow-sm hover:bg-gray-50 transition-colors'
                    >
                        <ChevronRight className='size-5 text-[#262628]' />
                    </button>

                    <button onClick={() => navigate(`/publications/${currentItem.id}`)} className='lg:hidden bg-primary hover:bg-primary-hover text-white font-semibold leading-[16px] p-4 rounded-[10px] transition-colors'>
                        詳細を見る
                    </button>

                    {/* Pagination Indicators */}
                    <div className='flex gap-3'>
                        {items.slice(0, 10).map((_, idx) => (
                            <button
                                key={idx}
                                onClick={() => {
                                    if (!isAnimating && idx !== currentIndex) {
                                        setIsAnimating(true);
                                        setCurrentIndex(idx);
                                    }
                                }}
                                className={cn(
                                    'w-2 h-2 rounded-full transition-all duration-300',
                                    currentIndex === idx ? 'bg-primary w-6' : 'bg-[#D9D9D9]'
                                )}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default FeaturedCarousel;
